import React, { useEffect, useState } from "react";
import {
  buildDocumentPreviewHtml,
  getDocumentPreviewMode,
} from "../../utils/documentPreview";
import "./documentPreview.scss";

/**
 * In-app document preview for blobs from document/viewfile.
 * Renders DOCX/XLSX as HTML; PDF/images use native embed.
 */
export default function DocumentPreview({
  blobUrl,
  documentType,
  documentName,
  className = "",
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [html, setHtml] = useState("");
  const [viewMode, setViewMode] = useState("embed");

  useEffect(() => {
    let cancelled = false;

    if (!blobUrl) {
      setLoading(false);
      setViewMode("embed");
      return undefined;
    }

    const nativeMode = getDocumentPreviewMode(documentType, documentName);
    if (nativeMode === "image" || nativeMode === "pdf" || nativeMode === "embed") {
      setViewMode(nativeMode);
      setHtml("");
      setError("");
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError("");
    setHtml("");

    buildDocumentPreviewHtml(blobUrl, documentType, documentName)
      .then((result) => {
        if (cancelled) return;
        if (result.mode === "html" && result.html) {
          setViewMode("html");
          setHtml(result.html);
        } else {
          setViewMode("embed");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setViewMode("error");
        setError(err?.message || "Could not load preview.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [blobUrl, documentType, documentName]);

  const wrapClass = `doc-preview-root ${className}`.trim();

  if (loading) {
    return (
      <div className={`${wrapClass} doc-preview-loading`}>Loading preview…</div>
    );
  }

  if (viewMode === "error" || error) {
    return <div className={`${wrapClass} doc-preview-error`}>{error}</div>;
  }

  if (viewMode === "image") {
    return (
      <div className={wrapClass}>
        <img
          src={blobUrl}
          alt={documentName || "Document preview"}
          className="doc-preview-image"
        />
      </div>
    );
  }

  if (viewMode === "html") {
    return (
      <div
        className={`${wrapClass} doc-preview-html`}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  return (
    <iframe
      src={blobUrl}
      type={documentType || undefined}
      title="Document preview"
      className={`${wrapClass} doc-preview-iframe`}
    />
  );
}
